import { ImportTarget } from "client/Api/ImportUsers/importUsers";
import { UserLicense } from "../FeaturePackages/featurePackageManagement";
import { environmentHttpClient as httpClient } from "../httpClient";
import { UserGroupOverviewModel } from "../UserGroupsManagement/userGroupsManagement";

export type RecycleBinUser = {
	userId: number;
	deletionDate: Date;
	permanentDeletionDate: Date;
};

export type PermanentDeleteUsersResponse = {
	failedUserIds: readonly number[];
};

export type MoveUsersToRecycleBinResponse = {
	failedUserIds: readonly number[];
	usersMovedToRecycleBin: readonly RecycleBinUser[];
};

export type RestoreUsersResponse = {
	failedUserIds: readonly number[];
};

export type UserModelWithLicense = {
	id: number;
	userPrincipalName: string;
	primaryEmail: string | null;
	secondaryEmail: string;
	firstName: string;
	lastName: string;
	jobTitle: string;
	department: string | null;
	officeLocation: string | null;
	mobilePhone: string;
	birthday: Date | string | null;
	isAdmin: boolean;
	isActive: boolean;
	isHiddenFromAddressBook: boolean;
	selectedUserGroups: number[] | null;
	userLicense: UserLicense;
};

export type Office365Domain = {
	domainName: string;
	isDefault: boolean;
};

export function getViewModel(
	signal?: AbortSignal
): Promise<UserManagementViewModel> {
	return httpClient.get<UserManagementViewModel>(
		"UiApi/UserManagement",
		undefined,
		{ signal }
	);
}

export function getEditViewModel(
	id: number,
	signal?: AbortSignal
): Promise<EditUserModel> {
	return httpClient.get<EditUserModel>(
		`UiApi/UserManagement/EditUser/${id}`,
		undefined,
		{ signal }
	);
}

export function getCreateViewModel(signal?: AbortSignal): Promise<EditUserModel> {
	return httpClient.get<EditUserModel>(
		"UiApi/UserManagement/CreateUser",
		undefined,
		{ signal }
	);
}

export function sendMoveUsersToRecycleBinRequest(
	userIds: readonly number[],
	signal?: AbortSignal
): Promise<MoveUsersToRecycleBinResponse> {
	return httpClient.delete<MoveUsersToRecycleBinResponse>(
		"UiApi/UserManagement/MoveUsersToRecycleBin",
		userIds,
		{ signal }
	);
}

export function sendRestoreUsersRequest(
	userIds: readonly number[],
	signal?: AbortSignal
): Promise<RestoreUsersResponse> {
	return httpClient.post<RestoreUsersResponse>(
		"UiApi/UserManagement/RestoreUsers",
		userIds,
		{ signal }
	);
}

export function sendPermanentDeleteUsersRequest(
	userIds: readonly number[],
	signal?: AbortSignal
): Promise<PermanentDeleteUsersResponse> {
	return httpClient.delete<PermanentDeleteUsersResponse>(
		"UiApi/UserManagement/PermanentDeleteUsers",
		userIds,
		{ signal }
	);
}

export type CreateNewUserModel = Omit<UserModelWithLicense, "userLicense"> & {
	userLicense: OptionalModel<UserLicense>;
};

type UpdateUserModelRestFields = Omit<
	UserModelWithLicense,
	"id"
		| "userLicense"
		| "isAdmin"
		| "isHiddenFromAddressBook"
>;

export type UpdateUserModel = {
	isAdmin: OptionalModel<boolean>;
	isHiddenFromAddressBook: OptionalModel<boolean>;
	userLicense: OptionalModel<UserLicense>;
	theRest: OptionalModel<UpdateUserModelRestFields>;
};

export function createNewUser(
	modelToSave: CreateNewUserModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"UiApi/UserManagement/User",
		modelToSave,
		{ signal }
	);
}

export function editUser(
	id: number,
	modelToSave: UpdateUserModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.put<void>(
		`UiApi/UserManagement/EditUser/${id}`,
		modelToSave,
		{ signal }
	);
}

export function getAvailableDomains(
	signal?: AbortSignal
): Promise<readonly Office365Domain[]> {
	return httpClient.get<readonly Office365Domain[]>(
		"UiApi/UserManagement/GetAvailableDomains",
		undefined,
		{ signal }
	);
}

export function getSettings(signal?: AbortSignal): Promise<UserManagementWelcomeEmailSettingModel> {
	return httpClient.get<UserManagementWelcomeEmailSettingModel>(
		"UiApi/UserManagement/GetWelcomeEmailSettings",
		undefined,
		{ signal }
	);
}

export function saveSettings(
	model: UserManagementWelcomeEmailSettingModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.put<void>(
		"UiApi/UserManagement/SaveWelcomeEmailSettings",
		model,
		{ signal }
	);
}

export function sendWelcomeEmail(
	userIds: readonly number[],
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"UiApi/UserManagement/SendWelcomeEmail",
		userIds,
		{ signal }
	);
}

export type UserManagementWelcomeEmailSettingModel = {
	sendAutomaticWelcomeEmail: boolean;
};

export type UserManagementActionsInfoModel = {
	allowCreate: boolean;
	allowDelete: boolean;
	allowImportFromOffice365: boolean;
	allowSendingWelcomeEmails: boolean;
};

export type UserGridItem = {
	id: number;
	pathToImage: string | null;
	primaryEmail: string | null;
	userPrincipalName: string;
	firstName: string;
	lastName: string;
	isActive: boolean;
	deletionDate: Date | null;
	permanentDeletionDate: Date | null;
	isAdmin: boolean;
	isGuest: boolean;
	userLicense: UserLicense;
};

export type UserManagementViewModel = {
	allUsers: UserGridItem[];
	currentUserId: number;
	userManagementActionsInfo: UserManagementActionsInfoModel;
	usersImportTarget: ImportTarget;
	canConfigureTheWelcomeEmailPreference: boolean;
	canConfigureCustomFields: boolean;
};

export enum EditableUserFields {
	None = 0,
	IsAdmin = 0x1 << 1,
	UserLicense = 0x1 << 2,
	TheRest = 0x1 << 3,
	IsHiddenFromAddressBook = 0x1 << 4
}

export type EditUserModel = {
	selectedUser: UserModelWithLicense;
	isCurrentUser: boolean;
	userGroups: UserGroupOverviewModel[];
	editUserFieldsMode: EditableUserFields;
};

export type UserManagementCustomFieldModel = {
	displayName: string | null;
	isEnabled: boolean;
};

export type UserManagementCustomFieldCollectionModel = {
	string1: UserManagementCustomFieldModel;
	string2: UserManagementCustomFieldModel;
	string3: UserManagementCustomFieldModel;
	string4: UserManagementCustomFieldModel;
	string5: UserManagementCustomFieldModel;
};

export type CustomStringSaveModel = {
	displayName: string;
	isEnabled: boolean;
};

export type UserManagementCustomStringsSaveModel = {
	string1: CustomStringSaveModel;
	string2: CustomStringSaveModel;
	string3: CustomStringSaveModel;
	string4: CustomStringSaveModel;
	string5: CustomStringSaveModel;
};

export function saveCustomStrings(
	model: UserManagementCustomStringsSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.put<void>(
		"UiApi/UserManagement/SaveCustomFieldsSettings",
		model,
		{ signal }
	);
}

export function getCustomStrings(
	signal?: AbortSignal
): Promise<UserManagementCustomFieldCollectionModel> {
	return httpClient.get<UserManagementCustomFieldCollectionModel>(
		"UiApi/UserManagement/GetCustomFieldsSettings",
		undefined,
		{ signal }
	);
}
